import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ExternalLink,
  Loader2,
  RefreshCw,
  Rocket,
  Play,
  Settings2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCatalogRun } from "@/hooks/useCatalogRun";
import { useCapabilityOverrides } from "@/hooks/useCapabilities";
import {
  fetchCatalogPending,
  fetchCatalogStale,
  fetchCatalogSweepPlan,
} from "@/services/nvgCatalogApi";
import type { CatalogRunState } from "@/types/nvgCatalog";
import { CatalogCronDialog } from "@/components/nvgCatalog/CatalogCronDialog";
import { CatalogSweepCard } from "@/components/nvgCatalog/CatalogSweepCard";
import {
  catalogCronBadgeKind,
  catalogCronRowStatus,
  selectGlobalRow,
  type CatalogCronBadgeKind,
  type CatalogCronRowStatus,
} from "@/utils/catalogCronBadge";

const DEFAULT_DELTA_LIMIT = 50;

function cloudWatchUrl(run: CatalogRunState): string | null {
  if (run.log_group === null || run.log_stream === null) return null;
  const region = "us-west-2";
  const group = encodeURIComponent(run.log_group);
  const stream = encodeURIComponent(run.log_stream);
  return (
    `https://${region}.console.aws.amazon.com/cloudwatch/home?region=${region}` +
    `#logsV2:log-groups/log-group/${group}/log-events/${stream}`
  );
}

function statusTone(
  status: CatalogRunState["status"],
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "succeeded":
      return "default";
    case "failed":
      return "destructive";
    // Neither of these is a failure: the lock was held so nothing was
    // attempted, or Spot reclaimed the task and re-running picks up where it
    // left off. Colouring them red would send someone debugging a non-event.
    case "already_running":
    case "interrupted":
      return "outline";
    // Both sweep-only, and both genuinely bad — but bad in different ways than
    // "failed", which is why they are named separately. `refused` means the
    // plan looked wrong and nothing was written; `partial` means rows went but
    // some S3 objects stayed behind.
    case "refused":
    case "partial":
      return "destructive";
    default:
      return "secondary";
  }
}

function catalogCronBadgeVariant(
  kind: CatalogCronBadgeKind,
): "default" | "secondary" | "destructive" | "outline" {
  switch (kind) {
    case "running":
      return "outline";
    case "paused":
    case "error":
      return "destructive";
    case "gate-paused":
    case "sweep-paused":
    case "pending":
      return "secondary";
  }
}

function catalogCronBadgeLabel(kind: CatalogCronBadgeKind): string {
  switch (kind) {
    case "running":
      return "Scheduled";
    case "gate-paused":
      return "Gate paused";
    case "sweep-paused":
      return "Sweep paused";
    case "paused":
      return "Paused";
    case "error":
      return "Error";
    case "pending":
      return "Loading…";
  }
}

function catalogCronRowVariant(
  status: CatalogCronRowStatus,
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "running":
      return "outline";
    case "paused":
      return "destructive";
    case "error":
      return "destructive";
    case "pending":
      return "secondary";
  }
}

function catalogCronRowLabel(status: CatalogCronRowStatus): string {
  switch (status) {
    case "running":
      return "Running";
    case "paused":
      return "Paused";
    case "error":
      return "Unknown — load failed";
    case "pending":
      return "Loading…";
  }
}

function StatNumber({
  label,
  value,
  hint,
  emphasis,
}: {
  label: string;
  value: number | undefined;
  hint: string;
  emphasis?: boolean;
}) {
  return (
    <div className="space-y-1">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div
        className={
          emphasis && value !== undefined && value > 0
            ? "text-3xl font-semibold text-amber-500"
            : "text-3xl font-semibold"
        }
      >
        {value === undefined ? <Skeleton className="h-8 w-16" /> : value}
      </div>
      <div className="text-xs text-muted-foreground">{hint}</div>
    </div>
  );
}

export default function NvgCatalog() {
  const [limit, setLimit] = useState<string>(String(DEFAULT_DELTA_LIMIT));
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [cronDialogOpen, setCronDialogOpen] = useState(false);

  const gateOverrides = useCapabilityOverrides("catalog.cron.gate");
  const sweepOverrides = useCapabilityOverrides("catalog.cron.sweep");
  const gateRow = useMemo(
    () => selectGlobalRow(gateOverrides.data),
    [gateOverrides.data],
  );
  const sweepRow = useMemo(
    () => selectGlobalRow(sweepOverrides.data),
    [sweepOverrides.data],
  );
  const gateOverridesPending =
    gateOverrides.isPending && gateOverrides.data === undefined;
  const sweepOverridesPending =
    sweepOverrides.isPending && sweepOverrides.data === undefined;
  const gateOverridesError =
    gateOverrides.isError && gateOverrides.data === undefined;
  const sweepOverridesError =
    sweepOverrides.isError && sweepOverrides.data === undefined;
  const cronBadge = catalogCronBadgeKind({
    gateRow,
    sweepRow,
    gatePending: gateOverridesPending,
    sweepPending: sweepOverridesPending,
    gateError: gateOverridesError,
    sweepError: sweepOverridesError,
  });
  const gateRowStatus = catalogCronRowStatus({
    row: gateRow,
    pending: gateOverridesPending,
    error: gateOverridesError,
  });
  const sweepRowStatus = catalogCronRowStatus({
    row: sweepRow,
    pending: sweepOverridesPending,
    error: sweepOverridesError,
  });

  const pending = useQuery({
    queryKey: ["nvg-catalog", "pending"],
    queryFn: fetchCatalogPending,
  });

  // Not fetched on mount alongside `pending`: the report costs an SSM round
  // trip and a container that reads every stored tube row. The operator asks
  // for it.
  const stale = useQuery({
    queryKey: ["nvg-catalog", "stale"],
    queryFn: () => fetchCatalogStale(false),
    enabled: false,
  });

  // Same argument as `stale`: the plan reads the live corpus and every stored
  // tube row over SSM. The operator asks for it. `refresh: true` because a
  // plan is what authorises a delete — serving a cached one on an explicit
  // "check" would let someone confirm against a number the server had already
  // let expire.
  const sweep = useQuery({
    queryKey: ["nvg-catalog", "sweep"],
    queryFn: () => fetchCatalogSweepPlan(true),
    enabled: false,
  });

  const { state, start, startSweep, reset } = useCatalogRun(() => {
    void pending.refetch();
    if (stale.data !== undefined) void stale.refetch();
    // A sweep changes exactly what the plan describes, and the server drops
    // its cached copy on dispatch — so a settled run leaves the panel showing
    // a plan that is definitely out of date until this lands.
    if (sweep.data !== undefined) void sweep.refetch();
  });

  const ceiling = pending.data?.delta_limit_ceiling ?? 200;
  const missing = pending.data?.missing;
  const parsedLimit = Number.parseInt(limit, 10);
  const limitValid = Number.isFinite(parsedLimit) && parsedLimit >= 1;

  // The server refuses this too — this is only so the operator finds out
  // before the round trip, not instead of it.
  const backlogBlocksDelta = missing !== undefined && missing > ceiling;
  const busy = state.phase === "submitting" || state.phase === "polling";

  const deltaDisabledReason = !limitValid
    ? "Enter a limit of at least 1."
    : backlogBlocksDelta
      ? `${missing} combos exceeds the ${ceiling} this shared instance may render. Use a bulk run.`
      : busy
        ? "A run is already in progress."
        : null;

  return (
    <TooltipProvider>
      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">Catalog Runs</h1>
          <p className="text-sm text-muted-foreground">
            Backlog and staleness for the exact-tube catalog, and the two ways
            to clear them.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {/* ── backlog ─────────────────────────────────────────────────── */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Missing</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void pending.refetch()}
                disabled={pending.isFetching}
              >
                <RefreshCw
                  className={
                    pending.isFetching ? "h-4 w-4 animate-spin" : "h-4 w-4"
                  }
                />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {pending.isError ? (
                <p className="text-sm text-destructive">
                  {(pending.error as Error).message}
                </p>
              ) : (
                <>
                  <StatNumber
                    label="Combos with no rows at all"
                    value={missing}
                    hint={`of ${pending.data?.total_combos ?? "—"} distinct combos across ${pending.data?.total_variants ?? "—"} live variants`}
                    emphasis
                  />
                  {pending.data !== undefined &&
                    pending.data.sanitized_variants > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {pending.data.sanitized_variants} variant(s) had an
                        out-of-bounds spec discarded before hashing, and{" "}
                        {pending.data.skipped_variants} have no usable spec at
                        all. Neither is a render backlog.
                      </p>
                    )}
                </>
              )}
            </CardContent>
          </Card>

          {/* ── staleness ───────────────────────────────────────────────── */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Stale</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void stale.refetch()}
                disabled={stale.isFetching}
              >
                {stale.isFetching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {stale.isError ? (
                <p className="text-sm text-destructive">
                  {(stale.error as Error).message}
                </p>
              ) : stale.data === undefined ? (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Rows that exist but no longer match what the renderer would
                    produce. Measured on the box by the simulator CLI — the API
                    cannot compute it, and the check is not free.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void stale.refetch()}
                    disabled={stale.isFetching}
                  >
                    Check staleness
                  </Button>
                </div>
              ) : (
                <>
                  <StatNumber
                    label="Rows needing a re-render"
                    value={stale.data.stale}
                    hint={`${stale.data.fresh} fresh of ${stale.data.total_tube_rows} stored tube rows${stale.data.cached ? " (cached)" : ""}`}
                    emphasis
                  />
                  {stale.data.missing_payload > 0 && (
                    <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
                      <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
                      <p className="text-xs">
                        {stale.data.missing_payload} row(s) store no content
                        payload. That usually means{" "}
                        <code>backfill_hashes_prod.sh</code> has not been run
                        against production — an operator action, not a render.
                        Run it first: a bulk run against this would re-render
                        the entire catalog.
                      </p>
                    </div>
                  )}
                  {stale.data.unreconstructable > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {stale.data.unreconstructable} row(s) have no tube params,
                      so their staleness is unknown. Fixed by{" "}
                      <code>backfill-params</code>, not by a render.
                    </p>
                  )}
                  {Object.keys(stale.data.causes).length > 0 && (
                    <div className="space-y-1">
                      <div className="text-xs font-medium">What moved</div>
                      {Object.entries(stale.data.causes).map(
                        ([cause, count]) => (
                          <div
                            key={cause}
                            className="flex justify-between text-xs text-muted-foreground"
                          >
                            <span className="font-mono">{cause}</span>
                            <span>{count}</span>
                          </div>
                        ),
                      )}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ── orphans ─────────────────────────────────────────────────────
            Third and last of the three numbers, and the one that is not a
            backlog: `missing` and `stale` are both answered by rendering,
            while these rows are unreachable and only deletion removes them.
            Given its own row rather than a third column because it carries a
            destructive control the other two do not. */}
        <CatalogSweepCard
          sweep={sweep}
          busy={busy}
          notPreviewed={state.sweepNotPreviewed}
          onDismissNotPreviewed={reset}
          onApply={(confirmReap) => void startSweep(confirmReap)}
        />

        {/* ── scheduled generation ────────────────────────────────────────── */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle>Scheduled generation</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCronDialogOpen(true)}
            >
              <Settings2 className="mr-2 h-4 w-4" />
              Manage schedule
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant={catalogCronBadgeVariant(cronBadge.kind)}>
                {catalogCronBadgeLabel(cronBadge.kind)}
              </Badge>
              {cronBadge.kind === "paused" && (
                <AlertTriangle className="h-4 w-4 text-destructive" />
              )}
            </div>
            <p
              className={
                cronBadge.kind === "paused"
                  ? "text-sm text-destructive"
                  : "text-sm text-muted-foreground"
              }
            >
              {cronBadge.title}
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1 rounded-md border p-3">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm font-medium">Delta gate</span>
                  <Badge variant={catalogCronRowVariant(gateRowStatus)}>
                    {catalogCronRowLabel(gateRowStatus)}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  Every 5 minutes — renders up to 200 missing combos when the
                  corpus moves.
                </p>
                {gateRow?.expires_at && (
                  <p className="text-xs text-muted-foreground">
                    Resumes {new Date(gateRow.expires_at).toLocaleString()}
                  </p>
                )}
              </div>
              <div className="space-y-1 rounded-md border p-3">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm font-medium">Nightly sweep</span>
                  <Badge variant={catalogCronRowVariant(sweepRowStatus)}>
                    {catalogCronRowLabel(sweepRowStatus)}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  02:00 UTC — reconciles every missing combo with no limit, then
                  tombstones orphaned rows. It withholds its own deletions when
                  too much of the catalog comes due at once; the Orphaned panel
                  above is how a human overrides that.
                </p>
                {sweepRow?.expires_at && (
                  <p className="text-xs text-muted-foreground">
                    Resumes {new Date(sweepRow.expires_at).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── the two runs ──────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle>Start a run</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-xs text-muted-foreground">
              Unaffected by the scheduled generation switches above — these are
              explicit manual runs and stay available even while the schedule is
              paused.
            </p>
            {/* delta */}
            <div className="space-y-3">
              <div className="flex items-baseline gap-2">
                <h3 className="font-medium">Run now</h3>
                <Badge variant="secondary">on the API box</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Renders up to the limit below on the shared instance that also
                serves customer traffic. Bounded, and refused outright when the
                backlog is larger than it should take. Does not address
                staleness.
              </p>
              <div className="flex items-end gap-3">
                <div className="space-y-1">
                  <Label htmlFor="delta-limit">Limit</Label>
                  <Input
                    id="delta-limit"
                    type="number"
                    min={1}
                    max={ceiling}
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                    className="w-28"
                  />
                </div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* span, because a disabled button fires no pointer
                        events and the tooltip is the whole explanation. */}
                    <span>
                      <Button
                        variant="secondary"
                        onClick={() => void start("delta", parsedLimit)}
                        disabled={deltaDisabledReason !== null}
                      >
                        <Play className="mr-2 h-4 w-4" />
                        Run now
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {deltaDisabledReason !== null && (
                    <TooltipContent>{deltaDisabledReason}</TooltipContent>
                  )}
                </Tooltip>
                <span className="pb-2 text-xs text-muted-foreground">
                  max {ceiling}
                </span>
              </div>
            </div>

            <Separator />

            {/* bulk — deliberately not styled like the button above. The whole
                uptime argument collapses if an operator reaches for "Run now"
                expecting it to handle a watermark change. */}
            <div className="space-y-3">
              <div className="flex items-baseline gap-2">
                <h3 className="font-medium">Run on Spot</h3>
                <Badge variant="destructive">costs money</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                A Fargate Spot task, off the box entirely. This is the only
                control that addresses staleness, and with no limit it can
                rewrite the whole catalog.
              </p>
              <Button
                variant="destructive"
                onClick={() => setConfirmBulk(true)}
                disabled={busy}
              >
                <Rocket className="mr-2 h-4 w-4" />
                Run on Spot
              </Button>
            </div>

            {/* The 409 redirect, as a button rather than an error toast. */}
            {state.backlogTooLarge !== null && (
              <div className="flex items-start gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-4">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                <div className="space-y-2">
                  <p className="text-sm">{state.backlogTooLarge.detail}</p>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      reset();
                      setConfirmBulk(true);
                    }}
                  >
                    Run on Spot instead
                  </Button>
                </div>
              </div>
            )}

            {state.phase === "error" && state.error !== null && (
              <p className="text-sm text-destructive">{state.error}</p>
            )}

            {/* live status */}
            {state.run !== null && (
              <div className="space-y-2 rounded-md border p-4">
                <div className="flex items-center gap-2">
                  <Badge variant={statusTone(state.run.status)}>
                    {state.run.status.replace("_", " ")}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {state.run.kind} run
                  </span>
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  {cloudWatchUrl(state.run) !== null && (
                    <a
                      className="ml-auto inline-flex items-center gap-1 text-sm underline"
                      href={cloudWatchUrl(state.run)!}
                      target="_blank"
                      rel="noreferrer"
                    >
                      CloudWatch <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
                <div className="font-mono text-xs break-all text-muted-foreground">
                  {state.run.run_id}
                </div>
                {state.run.status === "already_running" && (
                  <p className="text-sm">
                    Another catalog run holds the lock — most likely the 02:00
                    sweep. Nothing was started, so nothing was lost.
                  </p>
                )}
                {state.run.status === "interrupted" && (
                  <p className="text-sm">
                    Spot reclaimed the task. Nothing is corrupted: the work list
                    comes from the database, so running again picks up where
                    this left off.
                  </p>
                )}
                {state.run.status === "refused" && (
                  <p className="text-sm">
                    A safety floor rejected the plan and{" "}
                    <strong>nothing was written</strong> — no tombstones, no
                    deletes. The corpus read itself looked wrong (no reachable
                    keys, or every row orphaned), which is a data problem rather
                    than a sweep problem. Do not simply run it again.
                  </p>
                )}
                {state.run.status === "partial" && (
                  <p className="text-sm">
                    The rows were deleted but some S3 objects were not. That is
                    leaked storage, never a broken reference — nothing can
                    resolve to an object whose row is gone. The failed keys are
                    in the output below; they need deleting by hand.
                  </p>
                )}
                {state.run.detail !== null && (
                  <pre className="max-h-64 overflow-auto rounded bg-muted p-2 text-xs whitespace-pre-wrap">
                    {state.run.detail}
                  </pre>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <CatalogCronDialog
          open={cronDialogOpen}
          onOpenChange={setCronDialogOpen}
          gateRow={gateRow}
          sweepRow={sweepRow}
        />

        <Dialog open={confirmBulk} onOpenChange={setConfirmBulk}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Start a bulk run on Fargate Spot?</DialogTitle>
              <DialogDescription>
                This costs real money per run and, without a limit, re-renders
                every missing and stale combo — potentially the whole catalog.
                {stale.data !== undefined && stale.data.missing_payload > 0 && (
                  <>
                    {" "}
                    {stale.data.missing_payload} row(s) currently store no
                    content payload, which usually means the production hash
                    backfill has not run. Doing that first avoids re-rendering
                    everything for nothing.
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmBulk(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setConfirmBulk(false);
                  void start("bulk", null);
                }}
              >
                Start bulk run
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
